// TeamsPage.js

import React, { useEffect, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useParams, Link } from 'react-router-dom';
import styles from './TeamsPage.module.css';
import { 
  FaUserAlt, FaTrophy, FaTrash, FaEdit, FaPlus, FaUsers,
  FaChartBar, FaBullseye, FaChartLine, FaMedal, FaFutbol,
  FaUserTie, FaUserShield, FaChevronRight, FaTimes,
  FaUserPlus, FaCheck, FaTimes as FaX, FaHandshake
} from 'react-icons/fa';
import { useUser } from '../context/UserContext';
import { useNotification } from '../context/NotificationContext';
import { canManageProgram } from '../hooks/useProgramsApi';
import { testTeamCreation } from '../utils/teamApiTest';
import TeamInvitationModal from '../components/TeamManagement/TeamInvitationModal';
import ConfirmationModal from '../components/ConfirmationModal/ConfirmationModal';
import { formatRole as formatRoleUtil, loadRolesForSport } from '../utils/roleFormatting';
import { sportRolesAPI } from '../api/sportRoles';
import { getDisplayName as getDisplayNameUtil, getAssistantCoaches, getPlayerUserId } from '../utils/teamDisplayHelpers';

// Skeleton Loading Component
const TeamCardSkeleton = () => (
  <div className={styles.teamCardSkeleton} aria-hidden="true">
    <div className={styles.skeletonHeader}>
      <div className={styles.skeletonTitle} />
      <div className={styles.skeletonActions} />
    </div>
    <div className={styles.skeletonKpi}>
      {[...Array(6)].map((_, i) => (
        <div key={i} className={styles.skeletonKpiItem} />
      ))}
    </div>
    <div className={styles.skeletonCoach} />
    <div className={styles.skeletonPlayers}>
      {[...Array(3)].map((_, i) => (
        <div key={i} className={styles.skeletonPlayerRow} />
      ))}
    </div>
  </div>
);

const TeamsPage = () => {
  const { clubId, programId } = useParams(); // Capture clubId and programId from the URL
  const { userClubs, userProfile } = useUser();
  const { showSuccess, showError, showWarning, showInfo } = useNotification();

  // Helper function to calculate team statistics from events
  const calculateTeamStats = (team) => {
    if (!events || events.length === 0) {
      return {
        games: '0W-0L-0D',
        score: '0-0',
        winRate: 0,
        players: `${team.players?.length || 0}/${team.maxPlayers || 0}`,
        achievements: team.achievements?.length || 0,
        pointsPerGame: '0.0'
      };
    }

    // Filter events for this team - improved matching logic
    const teamEvents = events.filter(event => {
      // Check if team is involved in this event
      if (event.teams && event.teams.home && event.teams.away) {
        // New structure: check if team name or ID matches home or away team
        const homeTeamMatch = (event.teams.home.name === team.name) || 
                             (event.teams.home._id === team._id) ||
                             (event.teams.home.teamId === team._id);
        const awayTeamMatch = (event.teams.away.name === team.name) ||
                             (event.teams.away._id === team._id) ||
                             (event.teams.away.teamId === team._id);
        
        return homeTeamMatch || awayTeamMatch;
      } else if (event.opponentTeam) {
        // Old structure: check if team is the main team or opponent
        const matches = event.opponentTeam === team._id || event.opponentTeam === team.name;
        return matches;
      }
      return false;
    });

    // Filter only completed games with scorecards (not practices)
    const completedGames = teamEvents.filter(event => {
      const isCompleted = event.status === 'Completed';
      const isGame = event.type && !event.type.toLowerCase().includes('practice');
      
      // Check for scorecard in multiple possible structures
      let hasScorecard = false;
      
      // Check for live session scorecard
      if (event.liveSession?.scorecard?.score && 
          event.liveSession.scorecard.score.home !== undefined && 
          event.liveSession.scorecard.score.away !== undefined) {
        hasScorecard = true;
      }
      // Check for final results
      else if (event.finalResults && 
               event.finalResults.homeScore !== undefined && 
               event.finalResults.awayScore !== undefined) {
        hasScorecard = true;
      }
      // Legacy: Check for scorecard in multiple possible structures
      else if (event.scorecard) {
        // Check if scorecard has score object with home/away
        if (event.scorecard.score && 
            (event.scorecard.score.home !== undefined && event.scorecard.score.away !== undefined)) {
          hasScorecard = true;
        }
        // Check if scorecard has home/away directly
        else if (event.scorecard.home !== undefined && event.scorecard.away !== undefined) {
          hasScorecard = true;
        }
        // Check if scorecard has finalScore
        else if (event.scorecard.finalScore) {
          hasScorecard = true;
        }
        // Check for any numeric values that could be scores
        else if (typeof event.scorecard === 'object') {
          const scorecardKeys = Object.keys(event.scorecard);
          // Look for any keys that might contain score data
          const possibleScoreKeys = ['home', 'away', 'homeScore', 'awayScore', 'score', 'finalScore', 'result'];
          const hasScoreData = scorecardKeys.some(key => 
            possibleScoreKeys.includes(key) && 
            (typeof event.scorecard[key] === 'number' || 
             (typeof event.scorecard[key] === 'string' && event.scorecard[key].includes('-')))
          );
          if (hasScoreData) {
            hasScorecard = true;
          }
        }
      }
      
      return isCompleted && isGame && hasScorecard;
    });

    // Calculate wins, losses, draws
    let wins = 0, losses = 0, draws = 0;
    let totalGoalsFor = 0, totalGoalsAgainst = 0;

    completedGames.forEach(event => {
      let home, away;
      
      // Extract scores from different possible scorecard structures
      // Check for live session scorecard first
      if (event.liveSession?.scorecard?.score && 
          event.liveSession.scorecard.score.home !== undefined && 
          event.liveSession.scorecard.score.away !== undefined) {
        // Structure: liveSession.scorecard.score.home/away
        home = event.liveSession.scorecard.score.home;
        away = event.liveSession.scorecard.score.away;
      }
      // Check for final results
      else if (event.finalResults && 
               event.finalResults.homeScore !== undefined && 
               event.finalResults.awayScore !== undefined) {
        // Structure: finalResults.homeScore/awayScore
        home = event.finalResults.homeScore;
        away = event.finalResults.awayScore;
      }
      // Legacy: Extract scores from different possible scorecard structures
      else if (event.scorecard) {
        if (event.scorecard.score && 
            event.scorecard.score.home !== undefined && 
            event.scorecard.score.away !== undefined) {
          // Structure: scorecard.score.home/away
          home = event.scorecard.score.home;
          away = event.scorecard.score.away;
        } else if (event.scorecard.home !== undefined && 
                   event.scorecard.away !== undefined) {
          // Structure: scorecard.home/away
          home = event.scorecard.home;
          away = event.scorecard.away;
        } else if (event.scorecard.finalScore) {
          // Structure: scorecard.finalScore
          const finalScore = event.scorecard.finalScore;
          if (typeof finalScore === 'string' && finalScore.includes('-')) {
            const [homeStr, awayStr] = finalScore.split('-');
            home = parseInt(homeStr.trim());
            away = parseInt(awayStr.trim());
          } else if (typeof finalScore === 'object') {
            home = finalScore.home;
            away = finalScore.away;
          }
        } else if (event.scorecard.homeScore !== undefined && 
                   event.scorecard.awayScore !== undefined) {
          // Structure: scorecard.homeScore/awayScore
          home = event.scorecard.homeScore;
          away = event.scorecard.awayScore;
        } else if (event.scorecard.result) {
          // Structure: scorecard.result (string format like "2-1")
          const result = event.scorecard.result;
          if (typeof result === 'string' && result.includes('-')) {
            const [homeStr, awayStr] = result.split('-');
            home = parseInt(homeStr.trim());
            away = parseInt(awayStr.trim());
          }
        } else {
          // Try to find any numeric values in the scorecard
          const scorecardKeys = Object.keys(event.scorecard);
          const possibleScoreKeys = ['home', 'away', 'homeScore', 'awayScore', 'score', 'finalScore', 'result'];
          
          for (const key of possibleScoreKeys) {
            if (event.scorecard[key] !== undefined) {
              if (typeof event.scorecard[key] === 'number') {
                // If it's a single number, we need to find the other score
                // This is a fallback - we'll assume it's the home score
                home = event.scorecard[key];
                // Look for the away score in other keys
                const awayKey = scorecardKeys.find(k => k !== key && possibleScoreKeys.includes(k));
                if (awayKey && typeof event.scorecard[awayKey] === 'number') {
                  away = event.scorecard[awayKey];
                  break;
                }
              } else if (typeof event.scorecard[key] === 'string' && event.scorecard[key].includes('-')) {
                const [homeStr, awayStr] = event.scorecard[key].split('-');
                home = parseInt(homeStr.trim());
                away = parseInt(awayStr.trim());
                break;
              }
            }
          }
        }
      }

      if (home === undefined || away === undefined) {
        return;
      }

      let teamScore, opponentScore;

      // Determine which score is the team's score
      if (event.teams && event.teams.home && event.teams.away) {
        // Check if team is home or away
        const isHomeTeam = (event.teams.home.name === team.name) || 
                          (event.teams.home._id === team._id) ||
                          (event.teams.home.teamId === team._id);
        
        if (isHomeTeam) {
          teamScore = home;
          opponentScore = away;
        } else {
          teamScore = away;
          opponentScore = home;
        }
      } else {
        // For old structure, assume team is home team
        teamScore = home;
        opponentScore = away;
      }

      totalGoalsFor += teamScore || 0;
      totalGoalsAgainst += opponentScore || 0;

      if (teamScore > opponentScore) {
        wins++;
      } else if (teamScore < opponentScore) {
        losses++;
      } else {
        draws++;
      }
    });

    const totalGames = wins + losses + draws;
    const winRate = totalGames > 0 ? Math.round((wins / totalGames) * 100) : 0;
    const pointsPerGame = totalGames > 0 ? (totalGoalsFor / totalGames).toFixed(1) : '0.0';

    return {
      games: `${wins}W-${losses}L-${draws}D`,
      score: `${totalGoalsFor}-${totalGoalsAgainst}`,
      winRate: winRate,
      players: `${team.players?.length || 0}/${team.maxPlayers || 0}`,
      achievements: team.achievements?.length || 0,
      pointsPerGame: pointsPerGame
    };
  };

  // Format role names using data-driven utility (roles loaded from database)
  const formatRole = formatRoleUtil;
  const [teams, setTeams] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTeam, setSelectedTeam] = useState(null); // For editing specific team
  // Removed roster view - only using grid view
  const [programSport, setProgramSport] = useState(null);

  // Dynamic player roles from database
  const [availablePlayerRoles, setAvailablePlayerRoles] = useState([
    { roleKey: 'player', roleName: 'Player' },
    { roleKey: 'captain', roleName: 'Captain' },
    { roleKey: 'vice-captain', roleName: 'Vice Captain' },
    { roleKey: 'goalkeeper', roleName: 'Goalkeeper' },
    { roleKey: 'defender', roleName: 'Defender' },
    { roleKey: 'midfielder', roleName: 'Midfielder' },
    { roleKey: 'forward', roleName: 'Forward' },
  ]);

  // Load sport-specific roles from database when sport is known
  useEffect(() => {
    if (programSport) {
      loadRolesForSport(programSport);
      // Also fetch roles for the team form dropdowns
      sportRolesAPI.getRoles(programSport, 'player').then((roles) => {
        if (roles && roles.length > 0) {
          const hasPlayer = roles.some(r => r.roleKey === 'player');
          setAvailablePlayerRoles(
            hasPlayer ? roles : [{ roleKey: 'player', roleName: 'Player' }, ...roles]
          );
        }
      }).catch(() => {});
    }
  }, [programSport]);

  // State for modal visibility
  const [showModal, setShowModal] = useState(false);
  const [showAchievementModal, setShowAchievementModal] = useState(false);
  const [showInvitationModal, setShowInvitationModal] = useState(false);
  const [selectedTeamForInvitation, setSelectedTeamForInvitation] = useState(null);
  const [showGamesModal, setShowGamesModal] = useState(false);
  const [modalTeam, setModalTeam] = useState(null);

  // Program permissions state
  const [programPermissions, setProgramPermissions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [resolvedClubId, setResolvedClubId] = useState(clubId);

  // Confirmation modal state
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState(null);

  // Authorization logic - similar to EventsPage
  const canManage = useMemo(() => {
    // Check if user has club management role
    const hasClubRole = Array.isArray(userClubs) && userClubs.some((m) => {
      const membershipClubId = m.club?._id || m.club;
      const role = (m.role || '').toLowerCase();
      const isTargetClub = String(membershipClubId) === String(resolvedClubId);
      const isOwnerOrManager = ['owner', 'creator', 'clubowner', 'clubmanager', 'manager', 'admin'].includes(role);
      return isTargetClub && isOwnerOrManager;
    });

    // Check if user has program management permissions
    const hasProgramPermissions = canManageProgram(programPermissions);

    return hasClubRole || hasProgramPermissions;
  }, [userClubs, resolvedClubId, programPermissions]);

  // Check if user can send team invitations (more specific than canManage)
  const canSendInvitations = useMemo(() => {
    // Check if user is a team coach or assistant coach
        const isTeamCoach = teams.some(team => {
          const coachId = team.coach?._id || team.coach;
          const assistantCoachIds = team.assistantCoaches?.map(ac => ac._id || ac) || [];
          return coachId === userProfile?._id || assistantCoachIds.includes(userProfile?._id);
        });

        // Check if user has program management permissions (program managers/directors)
        const hasProgramPermissions = canManageProgram(programPermissions);

        // Check if user has club management role (club managers can invite)
        const hasClubRole = Array.isArray(userClubs) && userClubs.some((m) => {
          const membershipClubId = m.club?._id || m.club;
          const role = (m.role || '').toLowerCase();
          const isTargetClub = String(membershipClubId) === String(resolvedClubId);
          const isOwnerOrManager = ['owner', 'creator', 'clubowner', 'clubmanager', 'manager', 'admin'].includes(role);
          return isTargetClub && isOwnerOrManager;
        });

        return isTeamCoach || hasProgramPermissions || hasClubRole;
  }, [teams, userProfile, programPermissions, userClubs, resolvedClubId]);

  // Build a map of playerId → teamName for players already on OTHER teams in this program.
  // When editing, exclude the currently-selected team so its own players remain selectable.
  const playerTeamMap = useMemo(() => {
    const map = {};
    const editingTeamId = selectedTeam?._id || null;

    const addToMap = (id, teamName) => {
      if (id) map[id] = teamName;
    };

    teams.forEach((team) => {
      if (team._id === editingTeamId) return; // skip the team being edited

      // Map players — store every possible ID variant to prevent mismatches
      if (team.players && Array.isArray(team.players)) {
        team.players.forEach((player) => {
          if (typeof player === 'string') {
            addToMap(player, team.name);
          } else {
            addToMap(player.userId, team.name);
            addToMap(player._id, team.name);
            addToMap(player.user?._id, team.name);
          }
        });
      }

      // Map coach
      if (team.coach) {
        if (typeof team.coach === 'string') {
          addToMap(team.coach, team.name);
        } else {
          addToMap(team.coach._id, team.name);
          addToMap(team.coach?.userId, team.name);
        }
      }

      // Map assistant coaches
      if (team.assistantCoaches && Array.isArray(team.assistantCoaches)) {
        team.assistantCoaches.forEach((ac) => {
          if (typeof ac === 'string') {
            addToMap(ac, team.name);
          } else {
            addToMap(ac._id, team.name);
            addToMap(ac.userId, team.name);
          }
        });
      }
    });

    return map;
  }, [teams, selectedTeam?._id]);

  // Fetch program details to get clubId and sport if not available in route params
  useEffect(() => {
    if (programId) {
      const fetchProgramDetails = async () => {
        try {
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/programs/${programId}`);
          if (response.ok) {
            const program = await response.json();
            // Extract clubId correctly (handle both string and object cases)
            const extractedClubId = program.clubId && typeof program.clubId === 'object' 
              ? program.clubId._id 
              : program.clubId;
            setResolvedClubId(extractedClubId);
            // Set program sport for role management
            setProgramSport(program.sport || 'Soccer'); // Fallback to 'Soccer' if no sport
          } else {
            // Fallback: Try to find matching club from user's memberships
            if (Array.isArray(userClubs) && userClubs.length > 0) {
              // Since we can't get the program details, we'll try to find a club where user is a manager
              const managerClub = userClubs.find(m => {
                const role = (m.role || '').toLowerCase();
                return ['owner', 'creator', 'clubowner', 'clubmanager', 'manager', 'admin'].includes(role);
              });
              
              if (managerClub) {
                const fallbackClubId = managerClub.club?._id || managerClub.club;
                setResolvedClubId(fallbackClubId);
              }
            }
            // Set a default sport if program fetch fails
            setProgramSport('Soccer');
          }
        } catch (error) {
          // Fallback: Try to find matching club from user's memberships
          if (Array.isArray(userClubs) && userClubs.length > 0) {
            // Since we can't get the program details, we'll try to find a club where user is a manager
            const managerClub = userClubs.find(m => {
              const role = (m.role || '').toLowerCase();
              return ['owner', 'creator', 'clubowner', 'clubmanager', 'manager', 'admin'].includes(role);
            });
            
            if (managerClub) {
              const fallbackClubId = managerClub.club?._id || managerClub.club;
              setResolvedClubId(fallbackClubId);
            }
          }
          // Set a default sport if program fetch fails
          setProgramSport('Soccer');
        }
      };
      
      fetchProgramDetails();
    } else if (clubId) {
      setResolvedClubId(clubId);
      // Set a default sport if no programId
      setProgramSport('Soccer');
    }
  }, [clubId, programId, userClubs]);

  // Fetch program permissions for current user
  useEffect(() => {
    if (!programId) return;
    
    const fetchProgramPermissions = async () => {
      try {
        setPermissionsLoading(true);
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/programs/${programId}/permissions`);
        if (response.ok) {
          const data = await response.json();
          setProgramPermissions(data?.permissions || []);
        } else {
          setProgramPermissions([]);
        }
      } catch (error) {
        setProgramPermissions([]);
      } finally {
        setPermissionsLoading(false);
      }
    };

    fetchProgramPermissions();
  }, [programId]);

  // State for team form data
  const [teamFormData, setTeamFormData] = useState({
    name: '',
    coach: '',
    assistantCoaches: [],
    players: [],
    maxPlayers: '',
    jerseyColors: [],
    jerseyNumbers: {},
    playerRoles: {},
    achievements: [],
    performanceMetrics: [],
  });

  // State for users (to select coach, assistant coaches, players)
  const [users, setUsers] = useState([]);

  useEffect(() => {
    const fetchTeamsData = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/teams/program/${programId}`
        );
        if (response.status === 404) {
          // No teams found
          setTeams([]);
        } else {
          const data = await response.json();
          // Ensure we always work with an array
          setTeams(Array.isArray(data) ? data : [data]);
        }
      } catch (error) {
        console.error('Error fetching teams:', error);
        setTeams([]);
      } finally {
        setLoading(false);
      }
    };

    const fetchEventsData = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/events/${programId}`);
        
        if (response.ok) {
          const eventsData = await response.json();
          setEvents(eventsData);
        } else {
          console.error('Events API error:', response.status, response.statusText);
          setEvents([]);
        }
      } catch (error) {
        console.error('Error fetching events:', error);
        setEvents([]);
      }
    };

    if (programId) {
      fetchTeamsData();
      fetchEventsData();
    }
  }, [programId]);

  // Initialize teamFormData when selectedTeam changes
  useEffect(() => {
    if (selectedTeam) {
      // Extract player roles from the players array (backend structure)
      const playerRoles = {};
      if (selectedTeam.players && Array.isArray(selectedTeam.players)) {
        selectedTeam.players.forEach(player => {
          if (player.userId && player.roleKey) {
            playerRoles[player.userId] = player.roleKey;
          } else if (player._id && player.roleKey) {
            playerRoles[player._id] = player.roleKey;
          }
        });
      }
      
      
      // Initialize form data with selected team data
      setTeamFormData({
        name: selectedTeam.name || '',
        coach: selectedTeam.coach?._id || selectedTeam.coach || '',
        assistantCoaches: selectedTeam.assistantCoaches
          ? selectedTeam.assistantCoaches.map((ac) => ac._id || ac)
          : [],
        players: selectedTeam.players ? selectedTeam.players.map((p) => p._id || p) : [],
        maxPlayers: selectedTeam.maxPlayers || '',
        jerseyColors: selectedTeam.jerseyColors || [],
        jerseyNumbers: selectedTeam.jerseyNumbers
          ? selectedTeam.jerseyNumbers.reduce((acc, item) => {
              acc[item.playerId] = item.jerseyNumber;
              return acc;
            }, {})
          : {},
        playerRoles: playerRoles,
        achievements: selectedTeam.achievements
          ? selectedTeam.achievements.map((achievement) => ({
              ...achievement,
              date: achievement.date
                ? achievement.date.split('T')[0]
                : '', // Format date to YYYY-MM-DD
            }))
          : [],
        performanceMetrics: selectedTeam.performanceMetrics || [],
      });
    } else {
      // If no selected team, reset to defaults for new team creation
      setTeamFormData({
        name: '',
        coach: '',
        assistantCoaches: [],
        players: [],
        maxPlayers: 7,
        jerseyColors: [],
        jerseyNumbers: {},
        playerRoles: {},
        achievements: [],
        performanceMetrics: [],
      });
    }
  }, [selectedTeam]);

  // Don't auto-open modal anymore - let users choose to create teams
  // useEffect(() => {
  //   if (!loading && teams.length === 0) {
  //     openModal();
  //   }
  // }, [loading, teams]);

  // Fetch eligible users for teams (program members + club management + program management)
  useEffect(() => {
    const fetchEligibleUsers = async () => {
      try {
        // Use backend endpoint that filters players based on role type
        // - Players (goalkeeper, striker, etc.) can only be on ONE team per program
        // - Coaches (coach, assistant coach) can be on MULTIPLE teams
        const excludeTeamId = selectedTeam && selectedTeam._id ? selectedTeam._id : null;
        const url = excludeTeamId 
          ? `${process.env.REACT_APP_BACKEND_URL}/api/teams/program/${programId}/available-players?excludeTeamId=${excludeTeamId}`
          : `${process.env.REACT_APP_BACKEND_URL}/api/teams/program/${programId}/available-players`;
        
        const response = await fetch(url);
        
        if (!response.ok) {
          throw new Error(`Failed to fetch available players: ${response.status}`);
        }
        
        const data = await response.json();
        const availablePlayers = data.availablePlayers || [];
        
        // Transform to match expected format
        const transformedUsers = availablePlayers.map(player => ({
          _id: player._id,
          name: player.fullName || player.name || `${player.firstName || ''} ${player.lastName || ''}`.trim(),
          fullName: player.fullName || player.name || `${player.firstName || ''} ${player.lastName || ''}`.trim(),
          displayName: player.displayName || `${player.fullName || player.name || 'Unknown'} (${player.roleContext || 'Player'})`,
          email: player.email,
          roles: player.roles || ['player'],
          programRole: player.roleContext || 'player',
          programStatus: player.programStatus || 'enrolled',
          playerProfile: player.playerProfile
        }));
        
        setUsers(transformedUsers);
        return;
      } catch (error) {
        console.error('Error fetching available players:', error);
        
        // Show error to user instead of falling back to broken old method
        showError('Failed to load available players. Please refresh the page and try again.');
        setUsers([]);
        return;
      }
      
      // REMOVED: Fallback to old method - it doesn't filter properly and causes duplicate player assignments
    };

    if (programId) {
      fetchEligibleUsers();
    }
  }, [programId, selectedTeam]); // Re-fetch when selectedTeam changes to get proper exclusions

  // Render loading skeleton
  const renderLoadingSkeleton = () => (
    <div className={styles.teamsGrid} role="status" aria-label="Loading teams">
      {[...Array(3)].map((_, i) => (
        <TeamCardSkeleton key={i} />
      ))}
      <span className={styles.srOnly}>Loading team data...</span>
    </div>
  );

  if (loading || permissionsLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.mainContent}>
          <div className={styles.pageHeader}>
            <div className={styles.pageTitleSection}>
              <h1 className={styles.pageTitle}>Teams Management</h1>
            </div>
          </div>
          {renderLoadingSkeleton()}
        </div>
      </div>
    );
  }

  const openModal = (team = null) => {
    // Only allow opening modal if user has management permissions
    if (!canManage) {
      showError('You need management permissions to create or edit teams.');
      return;
    }
    setSelectedTeam(team);
    setShowModal(true);
  };

  const openInvitationModal = (team) => {
    setSelectedTeamForInvitation(team);
    setShowInvitationModal(true);
  };

  const closeModal = () => {
    setSelectedTeam(null);
    setShowModal(false);
  };

  const openAchievementModal = (team) => {
    setModalTeam(team);
    setShowAchievementModal(true);
  };

  const closeAchievementModal = () => {
    setShowAchievementModal(false);
    setModalTeam(null);
  };

  const openGamesModal = (team) => {
    setModalTeam(team);
    setShowGamesModal(true);
  };

  const closeGamesModal = () => {
    setShowGamesModal(false);
    setModalTeam(null);
  };

  // Open delete confirmation modal
  const openDeleteConfirmation = (team) => {
    // Only allow deletion if user has management permissions
    if (!canManage) {
      showError('You need management permissions to delete teams.');
      return;
    }

    setTeamToDelete(team);
    setShowConfirmDelete(true);
  };

  // Delete team function with event protection
  const deleteTeam = async () => {
    if (!teamToDelete) return;

    const teamId = teamToDelete._id;
    const teamName = teamToDelete.name;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/teams/${teamId}`,
        {
          method: 'DELETE',
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        
        // Handle 403 Forbidden
        if (response.status === 403) {
          showError('You don\'t have permission to delete this team.');
          setShowConfirmDelete(false);
          setTeamToDelete(null);
          return;
        }
        
        // Handle 400 Bad Request - Team is used in events
        if (response.status === 400 && errorData.details?.events) {
          const { eventsCount, events } = errorData.details;
          
          // Format event list for display
          const eventList = events
            .map(event => {
              const eventDate = event.date ? new Date(event.date).toLocaleDateString() : 'No date';
              return `• ${event.title} (${event.type}) - ${eventDate}`;
            })
            .join('\n');
          
          showError(
            `Cannot delete "${teamName}"\n\n` +
            `This team is currently assigned to ${eventsCount} event(s):\n\n` +
            eventList +
            '\n\nPlease remove the team from these events first, then try again.',
            { duration: 10000 }
          );
          setShowConfirmDelete(false);
          setTeamToDelete(null);
          return;
        }
        
        // Generic error handling
        throw new Error(errorData.message || 'Failed to delete team');
      }

      // Remove team from local state
      setTeams(prevTeams => prevTeams.filter(team => team._id !== teamId));
      showSuccess(`"${teamName}" deleted successfully!`);
      setShowConfirmDelete(false);
      setTeamToDelete(null);
    } catch (error) {
      console.error('Error deleting team:', error);
      showError(`An error occurred while deleting the team: ${error.message || 'Please try again.'}`);
      setShowConfirmDelete(false);
      setTeamToDelete(null);
    }
  };

  // Refresh teams after successful operations
  const refreshTeams = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/teams/program/${programId}`
      );
      if (response.ok) {
        const data = await response.json();
        setTeams(Array.isArray(data) ? data : [data]);
      }
    } catch (error) {
      console.error('Error refreshing teams:', error);
    }
  };

  // Helper function to format user display name with role context
  const formatUserDisplay = (user) => {
    // Use displayName if available (from backend PlayerProfile integration)
    if (user.displayName) {
      return user.displayName;
    }
    
    // Enhanced name extraction logic
    let name = 'Unknown User';
    if (user.fullName) {
      name = user.fullName;
    } else if (user.name) {
      name = user.name;
    } else if (user.firstName || user.lastName) {
      name = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
    } else if (user.email) {
      name = user.email;
    }
    
    // Add role context
    let roleContext = '';
    if (user.programRole === 'club_management') {
      roleContext = ` (Club ${user.clubRole || 'Management'})`;
    } else if (user.programRole && user.programRole !== 'player') {
      roleContext = ` (${user.programRole})`;
    } else if (user.programStatus === 'enrolled') {
      roleContext = ' (Program Member)';
    } else if (user.programRole === 'club_member') {
      roleContext = ' (Club Member)';
    }
    
    return `${name}${roleContext}`;
  };

  const getDisplayName = getDisplayNameUtil;

  // All users from the available players endpoint can fill any role
  // The backend already handles filtering for eligible users

  // Handle input changes for text and number fields
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setTeamFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle selection changes for assistant coaches
  const handleAssistantCoachesChange = (e) => {
    const options = e.target.options;
    const selectedAssistantCoaches = [];
    for (let i = 0; i < options.length; i++) {
      if (options[i].selected) {
        selectedAssistantCoaches.push(options[i].value);
      }
    }
    setTeamFormData((prev) => ({
      ...prev,
      assistantCoaches: selectedAssistantCoaches,
    }));
  };

  // Handle selection changes for players
  const handlePlayersChange = (e) => {
    const options = e.target.options;
    const selectedPlayers = [];
    for (let i = 0; i < options.length; i++) {
      if (options[i].selected) {
        selectedPlayers.push(options[i].value);
      }
    }
    setTeamFormData((prev) => ({
      ...prev,
      players: selectedPlayers,
    }));
  };

  // Handle jersey colors input
  const handleJerseyColorsChange = (e) => {
    const value = e.target.value;
    const colorsArray = value.split(',').map((color) => color.trim());
    setTeamFormData((prev) => ({
      ...prev,
      jerseyColors: colorsArray,
    }));
  };

  // Handle jersey number changes
  const handleJerseyNumberChange = (e) => {
    const { name, value } = e.target; // name is jerseyNumber_playerId
    const playerId = name.split('_')[1];
    setTeamFormData((prev) => ({
      ...prev,
      jerseyNumbers: {
        ...prev.jerseyNumbers,
        [playerId]: value,
      },
    }));
  };

  // Handle player role changes
  const handlePlayerRoleChange = (playerId, role) => {
    setTeamFormData((prev) => ({
      ...prev,
      playerRoles: {
        ...prev.playerRoles,
        [playerId]: role,
      },
    }));
  };

  // Handle achievements changes
  const handleAddAchievement = () => {
    setTeamFormData((prev) => ({
      ...prev,
      achievements: [
        ...prev.achievements,
        { title: '', date: '', description: '' },
      ],
    }));
  };

  const handleRemoveAchievement = (index) => {
    setTeamFormData((prev) => ({
      ...prev,
      achievements: prev.achievements.filter((_, i) => i !== index),
    }));
  };

  const handleAchievementChange = (e, index) => {
    const { name, value } = e.target;
    setTeamFormData((prev) => {
      const achievements = [...prev.achievements];
      achievements[index] = {
        ...achievements[index],
        [name]: value,
      };
      return { ...prev, achievements };
    });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if user has management permissions
    if (!canManage) {
      showError('You need management permissions to create or edit teams.');
      return;
    }
    
    try {
      // Prepare the payload
      const payload = {
        programId: programId, // Use programId from useParams
        name: teamFormData.name,
        coach: teamFormData.coach,
        assistantCoaches: teamFormData.assistantCoaches,
        players: teamFormData.players.map(playerId => {
          const roleKey = teamFormData.playerRoles[playerId] || 'player';
          // Get roleName from the API-driven roles data first, then fallback to formatRole
          const matchedRole = availablePlayerRoles.find(r => r.roleKey === roleKey);
          return {
            userId: playerId,
            roleKey,
            roleName: matchedRole?.roleName || formatRole(roleKey),
          };
        }),
        maxPlayers: teamFormData.maxPlayers,
        jerseyColors: teamFormData.jerseyColors,
        jerseyNumbers: Object.entries(teamFormData.jerseyNumbers).map(
          ([playerId, jerseyNumber]) => ({
            playerId,
            jerseyNumber: Number(jerseyNumber),
          })
        ),
        achievements: teamFormData.achievements,
        performanceMetrics: teamFormData.performanceMetrics,
      };


      // Validate required fields
      if (!payload.name || !payload.programId || !payload.coach) {
        showError('Team name, program ID, and head coach are required.');
        return;
      }

     

      // Ensure maxPlayers is a number
      if (payload.maxPlayers && typeof payload.maxPlayers !== 'number') {
        payload.maxPlayers = parseInt(payload.maxPlayers);
      }

      // Filter out empty jersey numbers
      payload.jerseyNumbers = payload.jerseyNumbers.filter(jn => jn.jerseyNumber && jn.jerseyNumber > 0);

      let response;
      if (selectedTeam && selectedTeam._id) {
        // Update existing team
        response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/teams/${selectedTeam._id}`,
          {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
          }
        );
      } else {
        // Create new team
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/teams`;
        response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        });
      }

      if (!response.ok) {
        console.error('Response status:', response.status);
        console.error('Response headers:', response.headers);
        
        if (response.status === 403) {
          showError('You don\'t have permission to perform this action.');
          return;
        }
        
        let errorData;
        try {
          errorData = await response.json();
          console.error('Error response data:', errorData);
        } catch (parseError) {
          console.error('Failed to parse error response:', parseError);
          const textResponse = await response.text();
          console.error('Raw error response:', textResponse);
          throw new Error(`Server error: ${response.status} ${response.statusText}`);
        }
        
        // Handle player assignment conflicts
        if (errorData.details && Array.isArray(errorData.details)) {
          const conflictMessages = errorData.details.map(conflict => 
            `${conflict.playerName || 'Player'} is already a ${conflict.conflictingRole || 'player'} on team "${conflict.conflictingTeam}". A player can only be on one team per program.`
          );
          showError(
            `Cannot save team:\n\n${conflictMessages.join('\n\n')}\n\nPlease remove these players or ask them to leave their current team first.`,
            { duration: 10000 }
          );
          return;
        }
        
        // Show detailed error message
        const errorMessage = errorData.message || errorData.error || 'Unknown server error';
        showError(`Error saving team: ${errorMessage}`);
        throw new Error(errorMessage);
      }

      // Refresh teams data
      await refreshTeams();
      closeModal();
      showSuccess(selectedTeam ? 'Team updated successfully!' : 'Team created successfully!');
    } catch (error) {
      console.error('Error saving team:', error);
      showError('An error occurred while saving the team. Please try again.');
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.mainContent}>
        <div className={styles.pageHeader}>
          <div className={styles.pageTitleSection}>
            <h1 className={styles.pageTitle}>Teams Management ({teams.length} teams)</h1>
            {programSport && (
              <span className={styles.sportBadge}>{programSport}</span>
            )}
          </div>
          
          <div className={styles.pageActions}>
            {/* Removed view mode toggle - only using grid view */}
          </div>
        </div>

        {teams.length > 0 ? (
          <div className={styles.teamsGrid} role="list" aria-label="Teams list">
            {teams.map((team) => {
              // Calculate team statistics
              const teamStats = calculateTeamStats(team);
              const winRateClass = teamStats.winRate >= 60 ? styles.winRateHigh : 
                                   teamStats.winRate >= 40 ? styles.winRateMid : styles.winRateLow;
              
              return (
                <article 
                  key={team._id} 
                  className={styles.teamCard}
                  role="listitem"
                  aria-label={`Team: ${team.name}`}
                >
                  {/* Team Header with Badge */}
                  <div className={styles.teamHeader}>
                    <div className={styles.teamBadge}>
                      <div className={styles.teamBadgeIcon}>
                        <FaTrophy />
                      </div>
                      <h2 className={styles.teamName}>{team.name}</h2>
                    </div>
                    {canManage && (
                      <div className={styles.teamActions} role="group" aria-label="Team actions">
                        {canSendInvitations && (
                          <button 
                            onClick={() => openInvitationModal(team)} 
                            className={styles.inviteButton}
                            title="Invite Players"
                            aria-label={`Invite players to ${team.name}`}
                          >
                            <FaUserPlus />
                          </button>
                        )}
                        <button 
                          onClick={() => openModal(team)} 
                          className={styles.editButton}
                          title="Edit Team"
                          aria-label={`Edit ${team.name}`}
                        >
                          <FaEdit />
                        </button>
                        <button 
                          onClick={() => openDeleteConfirmation(team)} 
                          className={styles.deleteButton}
                          title="Delete Team"
                          aria-label={`Delete ${team.name}`}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Win Rate Progress Ring */}
                  <div className={styles.winRateRing}>
                    <svg className={styles.progressRing} viewBox="0 0 36 36">
                      <path
                        className={styles.progressBg}
                        d="M18 2.0845
                           a 15.9155 15.9155 0 0 1 0 31.831
                           a 15.9155 15.9155 0 0 1 0 -31.831"
                      />
                      <path
                        className={`${styles.progressBar} ${winRateClass}`}
                        strokeDasharray={`${teamStats.winRate}, 100`}
                        d="M18 2.0845
                           a 15.9155 15.9155 0 0 1 0 31.831
                           a 15.9155 15.9155 0 0 1 0 -31.831"
                      />
                    </svg>
                    <div className={styles.winRateText}>
                      <span className={styles.winRateValue}>{teamStats.winRate}%</span>
                      <span className={styles.winRateLabel}>Win Rate</span>
                    </div>
                  </div>

                  {/* KPI Stats Grid */}
                  <div className={styles.kpiStats} role="group" aria-label="Team statistics">
                    <div className={styles.kpiCompact}>
                      <button 
                        className={styles.kpiItem} 
                        onClick={() => openGamesModal(team)}
                        aria-label={`Games: ${teamStats.games}. Click to view game history.`}
                      >
                        <span className={styles.kpiIcon}><FaChartBar /></span>
                        <div className={styles.kpiText}>
                          <span className={styles.kpiLabel}>Record</span>
                          <span className={styles.kpiValue}>{teamStats.games}</span>
                        </div>
                        <FaChevronRight className={styles.kpiArrow} />
                      </button>
                      
                      <div className={styles.kpiItem} aria-label={`Score: ${teamStats.score}`}>
                        <span className={styles.kpiIcon}><FaBullseye /></span>
                        <div className={styles.kpiText}>
                          <span className={styles.kpiLabel}>Goals</span>
                          <span className={styles.kpiValue}>{teamStats.score}</span>
                        </div>
                      </div>
                      
                      <div className={styles.kpiItem} aria-label={`Players: ${teamStats.players}`}>
                        <span className={styles.kpiIcon}><FaUsers /></span>
                        <div className={styles.kpiText}>
                          <span className={styles.kpiLabel}>Roster</span>
                          <span className={styles.kpiValue}>{teamStats.players}</span>
                        </div>
                      </div>
                      
                      <button 
                        className={styles.kpiItem} 
                        onClick={() => openAchievementModal(team)}
                        aria-label={`${teamStats.achievements} achievements. Click to view.`}
                      >
                        <span className={styles.kpiIcon}><FaMedal /></span>
                        <div className={styles.kpiText}>
                          <span className={styles.kpiLabel}>Awards</span>
                          <span className={styles.kpiValue}>{teamStats.achievements}</span>
                        </div>
                        <FaChevronRight className={styles.kpiArrow} />
                      </button>
                      
                      <div className={styles.kpiItem} aria-label={`Points per game: ${teamStats.pointsPerGame}`}>
                        <span className={styles.kpiIcon}><FaFutbol /></span>
                        <div className={styles.kpiText}>
                          <span className={styles.kpiLabel}>Pts/Game</span>
                          <span className={styles.kpiValue}>{teamStats.pointsPerGame}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Coaching Staff */}
                  <div className={styles.coachSection}>
                    <div className={styles.coachBlock}>
                      <div className={styles.coachTitle}>
                        <FaUserTie className={styles.coachTitleIcon} /> Head Coach
                      </div>
                      <Link
                        to={`/player-profile/${getPlayerUserId(team.coach)}`}
                        className={styles.coachCompact}
                        style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}
                      >
                        <span className={styles.coachName}>{getDisplayName(team.coach)}</span>
                      </Link>
                    </div>
                    {(() => {
                      const asstList = getAssistantCoaches(team);
                      return asstList.length > 0 && (
                        <div className={styles.assistantCoachBlock}>
                          <div className={styles.assistantCoachTitle}>
                            <FaUserShield className={styles.coachTitleIcon} />
                            Asst. Coach{asstList.length > 1 ? 'es' : ''}
                          </div>
                          {asstList.map((aCoach, index) => (
                            <Link
                              key={aCoach._id || index}
                              to={`/player-profile/${getPlayerUserId(aCoach)}`}
                              className={styles.assistantCoachCompact}
                              style={{ textDecoration: 'none', color: 'inherit', cursor: 'pointer' }}
                            >
                              <span className={styles.assistantCoachName}>{getDisplayName(aCoach)}</span>
                            </Link>
                          ))}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Players Table */}
                  <div className={styles.playersTable} role="table" aria-label="Team roster">
                    <div className={styles.playersHeader} role="row">
                      <div className={styles.colName} role="columnheader">Player</div>
                      <div className={styles.colRole} role="columnheader">Position</div>
                      <div className={styles.colJersey} role="columnheader">#</div>
                    </div>
                    <div className={styles.playersBody} role="rowgroup">
                      {team.players && team.players.length > 0 ? (
                        team.players
                          .filter(player => {
                            const playerRole = player.roleKey || team.playerRoles?.[player._id] || 'player';
                            return playerRole !== 'coach' && playerRole !== 'assistant-coach';
                          })
                          .map((player) => {
                            const playerUserId = getPlayerUserId(player);

                            let jerseyNumber = null;
                            if (team.jerseyNumbers && Array.isArray(team.jerseyNumbers)) {
                              const jerseyData = team.jerseyNumbers.find(
                                (jn) => jn.playerId && (
                                  jn.playerId.toString() === playerUserId?.toString() ||
                                  jn.playerId.toString() === player._id.toString()
                                )
                              );
                              jerseyNumber = jerseyData?.jerseyNumber;
                            }
                            
                            let playerRole = 'player';
                            if (player.roleKey) {
                              playerRole = player.roleKey;
                            } else if (team.playerRoles && team.playerRoles[player._id]) {
                              playerRole = team.playerRoles[player._id];
                            }
                            
                            return (
                              <Link
                                key={player._id}
                                to={`/player-profile/${playerUserId}`}
                                className={styles.playerRow}
                                role="row"
                                style={{ textDecoration: 'none', color: 'inherit' }}
                              >
                                <div className={styles.colName} role="cell">
                                  <span className={styles.playerAvatar}>
                                    {getDisplayName(player).charAt(0).toUpperCase()}
                                  </span>
                                  {getDisplayName(player)}
                                </div>
                                <div className={styles.colRole} role="cell">
                                  <span className={`${styles.roleBadge} ${styles[`role${playerRole.replace('-', '')}`] || ''}`}>
                                    {formatRole(playerRole)}
                                  </span>
                                </div>
                                <div className={styles.colJersey} role="cell">
                                  {jerseyNumber ? (
                                    <span className={styles.jerseyBadge}>{jerseyNumber}</span>
                                  ) : (
                                    <span className={styles.jerseyEmpty}>—</span>
                                  )}
                                </div>
                              </Link>
                            );
                          })
                      ) : (
                        <div className={styles.emptyPlayersRow}>
                          <FaUsers className={styles.emptyPlayersIcon} />
                          <span>No players assigned yet</span>
                        </div>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <div className={styles.emptyState} role="status">
            <div className={styles.emptyStateIcon}>
              <FaTrophy />
            </div>
            <h2 className={styles.emptyStateTitle}>Build Your First Team</h2>
            <p className={styles.emptyStateText}>
              Create teams to organize players, track performance, and manage your roster effectively.
            </p>
            <div className={styles.emptyStateFeatures}>
              <div className={styles.emptyFeature}>
                <FaUsers /> Organize players
              </div>
              <div className={styles.emptyFeature}>
                <FaChartBar /> Track statistics
              </div>
              <div className={styles.emptyFeature}>
                <FaMedal /> Record achievements
              </div>
            </div>
            {canManage && (
              <button 
                onClick={() => openModal()} 
                className={styles.createFirstTeamButton}
                aria-label="Create your first team"
              >
                <FaPlus />
                Create First Team
              </button>
            )}
          </div>
        )}

        {/* Floating Add Button */}
        {canManage && (
          <button 
            onClick={() => openModal()} 
            className={styles.addTeamButton} 
            title="Create New Team" 
            aria-label="Create New Team"
          >
            <FaPlus />
          </button>
        )}

        {/* Team Modification Modal */}
        {showModal && createPortal(
          <div className={styles.modalOverlay} role="dialog" aria-modal="true" aria-labelledby="team-modal-title">
            <div className={styles.modal}>
              <div className={styles.modalHeader}>
                <div className={styles.modalTitle} id="team-modal-title">
                  <FaUsers className={styles.modalTitleIcon} />
                  {selectedTeam && selectedTeam._id ? 'Edit Team' : 'Create New Team'}
                </div>
                <div className={styles.headerControls}>
                  <button
                    type="button"
                    className={styles.closeButton}
                    onClick={closeModal}
                    title="Close"
                    aria-label="Close modal"
                  >
                    <FaTimes />
                  </button>
                </div>
              </div>
              <div className={styles.modalBody}>
                <form className={styles.form} onSubmit={handleSubmit}>
                  {/* Modern Dashboard Layout */}
                  <div className={styles.modernDashboard}>
                    {/* Team Info Section */}
                    <div className={styles.formSection}>
                      <div className={styles.sectionHeader}>
                        <FaTrophy className={styles.sectionIcon} />
                        <span>Team Information</span>
                      </div>
                      <div className={styles.dashboardRow}>
                        <div className={styles.infoCard}>
                          <div className={styles.cardLabel}>
                            <FaEdit className={styles.labelIcon} /> Team Name
                          </div>
                          <input
                            type="text"
                            name="name"
                            value={teamFormData.name}
                            onChange={handleInputChange}
                            className={styles.modernInput}
                            placeholder="Enter team name..."
                            required
                            aria-label="Team name"
                          />
                        </div>
                        
                        <div className={styles.infoCard}>
                          <div className={styles.cardLabel}>
                            <FaUsers className={styles.labelIcon} /> Max Players
                          </div>
                          <input
                            type="number"
                            name="maxPlayers"
                            value={teamFormData.maxPlayers}
                            onChange={handleInputChange}
                            className={styles.modernInput}
                            placeholder="11"
                            required
                            aria-label="Maximum players"
                          />
                        </div>
                        
                        <div className={styles.infoCard}>
                          <div className={styles.cardLabel}>
                            <FaFutbol className={styles.labelIcon} /> Jersey Colors
                          </div>
                          <input
                            type="text"
                            name="jerseyColors"
                            value={teamFormData.jerseyColors.join(', ')}
                            onChange={handleJerseyColorsChange}
                            className={styles.modernInput}
                            placeholder="Blue, White"
                            aria-label="Jersey colors separated by comma"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Team Roster Section */}
                    <div className={styles.rosterSection}>
                      <div className={styles.rosterHeader}>
                        <div className={styles.rosterTitle}>
                          <FaUsers className={styles.rosterIcon} />
                          Team Roster
                        </div>
                        <div className={styles.rosterCounter}>
                          <span className={styles.rosterCount}>{teamFormData.players.length}</span>
                          <span className={styles.rosterDivider}>/</span>
                          <span className={styles.rosterMax}>{teamFormData.maxPlayers || 0}</span>
                        </div>
                      </div>
                      
                      {/* Player Selection */}
                      <div className={styles.addPlayersArea}>
                        <div className={styles.playerSelectionHeader}>
                          <div className={styles.headerColumn}>Select</div>
                          <div className={styles.headerColumn}>Player Name</div>
                          <div className={styles.headerColumn}>Role</div>
                          <div className={styles.headerColumn}>Jersey #</div>
                          <div className={styles.headerColumn}>Coach</div>
                          <div className={styles.headerColumn}>Asst. Coach</div>
                        </div>
                        
                        <div className={styles.playerSelectionList}>
                          {users.map((user) => {
                            const isSelected = teamFormData.players.includes(user._id);
                            const isCoach = teamFormData.coach === user._id;
                            const isAssistantCoach = teamFormData.assistantCoaches.includes(user._id);
                            const currentRole = teamFormData.playerRoles?.[user._id] || '';
                            const assignedTeamName = playerTeamMap[user._id];
                            const isUnavailable = !!assignedTeamName;
                            
                            return (
                              <div key={user._id} className={`${styles.playerSelectionRow} ${isSelected ? styles.selected : ''} ${isUnavailable ? styles.playerUnavailable : ''}`}>
                                {/* Checkbox Column */}
                                <div className={styles.checkboxColumn}>
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    disabled={isUnavailable}
                                    onChange={(e) => {
                                      if (isUnavailable) return;
                                      if (e.target.checked) {
                                        // Add to roster
                                        setTeamFormData(prev => ({
                                          ...prev,
                                          players: [...prev.players, user._id],
                                          playerRoles: { ...prev.playerRoles, [user._id]: 'player' }
                                        }));
                                      } else {
                                        // Remove from roster and clear all roles
                                        setTeamFormData(prev => ({
                                          ...prev,
                                          players: prev.players.filter(id => id !== user._id),
                                          playerRoles: { ...prev.playerRoles, [user._id]: undefined },
                                          jerseyNumbers: { ...prev.jerseyNumbers, [user._id]: undefined },
                                          coach: prev.coach === user._id ? '' : prev.coach,
                                          assistantCoaches: prev.assistantCoaches.filter(id => id !== user._id)
                                        }));
                                      }
                                    }}
                                    className={styles.rosterCheckbox}
                                  />
                                </div>
                                
                                {/* Player Name Column */}
                                <div className={styles.playerNameColumn}>
                                  <div className={styles.playerNameContainer}>
                                    <span className={`${styles.playerInitial} ${isUnavailable ? styles.playerInitialUnavailable : ''}`}>
                                      {(user.fullName || user.name || user.firstName || 'U').charAt(0).toUpperCase()}
                                    </span>
                                    <span className={`${styles.playerName} ${isUnavailable ? styles.playerNameUnavailable : ''}`}>
                                      {user.fullName || user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim()}
                                    </span>
                                    {isUnavailable ? (
                                      <span className={styles.unavailableBadge} title={`Already assigned to "${assignedTeamName}"`}>
                                        On "{assignedTeamName}"
                                      </span>
                                    ) : isSelected ? (
                                      <div className={styles.roleBadges}>
                                        {isCoach && (
                                          <span className={`${styles.roleBadge} ${styles.coachBadge}`}>
                                            <FaUserTie /> Coach
                                          </span>
                                        )}
                                        {isAssistantCoach && (
                                          <span className={`${styles.roleBadge} ${styles.assistantCoachBadge}`}>
                                            <FaUserShield /> Asst.
                                          </span>
                                        )}
                                      </div>
                                    ) : null}
                                  </div>
                                </div>
                                
                                {/* Role Column */}
                                <div className={styles.roleColumn}>
                                  {isSelected && (
                                    <select
                                      value={currentRole}
                                      onChange={(e) => handlePlayerRoleChange(user._id, e.target.value)}
                                      className={styles.roleSelect}
                                      disabled={isCoach || isAssistantCoach}
                                    >
                                      {availablePlayerRoles.map((role) => (
                                        <option key={role.roleKey} value={role.roleKey}>
                                          {role.roleName}
                                        </option>
                                      ))}
                                    </select>
                                  )}
                                </div>
                                
                                {/* Jersey Number Column */}
                                <div className={styles.jerseyColumn}>
                                  {isSelected && (
                                    <input
                                      type="number"
                                      value={teamFormData.jerseyNumbers[user._id] || ''}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        setTeamFormData(prev => ({
                                          ...prev,
                                          jerseyNumbers: {
                                            ...prev.jerseyNumbers,
                                            [user._id]: value ? Number(value) : undefined
                                          }
                                        }));
                                      }}
                                      className={styles.jerseyInput}
                                      placeholder="#"
                                      min="0"
                                      max="99"
                                      disabled={isCoach || isAssistantCoach}
                                    />
                                  )}
                                </div>
                                
                                {/* Coach Column */}
                                <div className={styles.coachColumn}>
                                  {isSelected && (
                                    <input
                                      type="radio"
                                      name="coach"
                                      checked={isCoach}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setTeamFormData(prev => ({
                                            ...prev,
                                            coach: user._id,
                                            playerRoles: { ...prev.playerRoles, [user._id]: 'coach' }
                                          }));
                                        }
                                      }}
                                      className={styles.coachRadio}
                                    />
                                  )}
                                </div>
                                
                                {/* Assistant Coach Column */}
                                <div className={styles.assistantCoachColumn}>
                                  {isSelected && (
                                    <input
                                      type="checkbox"
                                      checked={isAssistantCoach}
                                      onChange={(e) => {
                                        if (e.target.checked) {
                                          setTeamFormData(prev => ({
                                            ...prev,
                                            assistantCoaches: [...prev.assistantCoaches, user._id],
                                            playerRoles: { ...prev.playerRoles, [user._id]: 'assistant-coach' }
                                          }));
                                        } else {
                                          setTeamFormData(prev => ({
                                            ...prev,
                                            assistantCoaches: prev.assistantCoaches.filter(id => id !== user._id),
                                            playerRoles: { ...prev.playerRoles, [user._id]: 'player' }
                                          }));
                                        }
                                      }}
                                      disabled={isCoach}
                                      className={styles.assistantCoachCheckbox}
                                    />
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                    {/* Achievements Section */}
                    <div className={styles.formSection}>
                      <div className={styles.sectionHeader}>
                        <FaMedal className={styles.sectionIcon} />
                        <span>Achievements</span>
                        <span className={styles.sectionCount}>{teamFormData.achievements.length}</span>
                      </div>
                      <div className={styles.achievementsArea}>
                        {teamFormData.achievements.length === 0 ? (
                          <div className={styles.noAchievements}>
                            <FaTrophy className={styles.noAchievementsEmptyIcon} />
                            <p>No achievements yet. Add awards and trophies to showcase your team's success!</p>
                          </div>
                        ) : (
                          <div className={styles.achievementsList}>
                            {teamFormData.achievements.map((achievement, index) => (
                              <div key={index} className={styles.achievementRow}>
                                <div className={styles.achievementRowIcon}>
                                  <FaTrophy />
                                </div>
                                <div className={styles.achievementRowFields}>
                                  <input
                                    type="text"
                                    name="title"
                                    value={achievement.title}
                                    onChange={(e) => handleAchievementChange(e, index)}
                                    className={styles.modernInput}
                                    placeholder="Achievement title"
                                    required
                                    aria-label="Achievement title"
                                  />
                                  <input
                                    type="date"
                                    name="date"
                                    value={achievement.date}
                                    onChange={(e) => handleAchievementChange(e, index)}
                                    className={styles.modernInput}
                                    required
                                    aria-label="Achievement date"
                                  />
                                  <input
                                    type="text"
                                    name="description"
                                    value={achievement.description}
                                    onChange={(e) => handleAchievementChange(e, index)}
                                    className={styles.modernInput}
                                    placeholder="Description"
                                    required
                                    aria-label="Achievement description"
                                  />
                                </div>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveAchievement(index)}
                                  className={styles.removeButton}
                                  aria-label={`Remove achievement: ${achievement.title}`}
                                >
                                  <FaTrash />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        <button 
                          type="button" 
                          onClick={handleAddAchievement} 
                          className={styles.addAchievementButton}
                        >
                          <FaPlus /> Add Achievement
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
              <div className={styles.modalFooter}>
                <button
                  type="button"
                  className={styles.cancelButton}
                  onClick={closeModal}
                >
                  <FaTimes /> Cancel
                </button>
                <button type="submit" className={styles.saveButton} onClick={handleSubmit}>
                  <FaCheck /> {selectedTeam && selectedTeam._id ? 'Save Changes' : 'Create Team'}
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Achievement Modal */}
        {showAchievementModal && createPortal(
          <div className={styles.modalOverlay} role="dialog" aria-modal="true" aria-labelledby="achievements-modal-title">
            <div className={styles.modal}>
              <div className={styles.modalHeader}>
                <h2 className={styles.modalTitle} id="achievements-modal-title">
                  <FaMedal className={styles.modalTitleIcon} />
                  {modalTeam?.name} - Achievements
                </h2>
                <button
                  className={styles.closeButton}
                  onClick={closeAchievementModal}
                  title="Close"
                  aria-label="Close achievements modal"
                >
                  <FaTimes />
                </button>
              </div>
              <div className={styles.modalBody}>
                <div className={styles.achievementsContainer}>
                  {modalTeam?.achievements && modalTeam.achievements.length > 0 ? (
                    <div className={styles.achievementsGrid}>
                      {modalTeam.achievements.map((achievement, index) => (
                        <div key={index} className={styles.achievementCard}>
                          <div className={styles.achievementIcon}>
                            <FaTrophy />
                          </div>
                          <div className={styles.achievementContent}>
                            <div className={styles.achievementHeader}>
                              <h3 className={styles.achievementTitle}>{achievement.title}</h3>
                              <span className={styles.achievementDate}>
                                {achievement.date ? new Date(achievement.date).toLocaleDateString() : 'No date'}
                              </span>
                            </div>
                            {achievement.description && (
                              <p className={styles.achievementDescription}>{achievement.description}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className={styles.noAchievementsMessage}>
                      <div className={styles.noAchievementsIcon}><FaTrophy /></div>
                      <h3>No Achievements Yet</h3>
                      <p>This team hasn't earned any achievements yet. Keep playing to unlock them!</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Games Modal */}
        {showGamesModal && createPortal(
          <div className={styles.modalOverlay} role="dialog" aria-modal="true" aria-labelledby="games-modal-title">
            <div className={styles.modal}>
              <div className={styles.modalHeader}>
                <h2 className={styles.modalTitle} id="games-modal-title">
                  <FaChartBar className={styles.modalTitleIcon} />
                  {modalTeam?.name} - Game History
                </h2>
                <button
                  className={styles.closeButton}
                  onClick={closeGamesModal}
                  title="Close"
                  aria-label="Close game history modal"
                >
                  <FaTimes />
                </button>
              </div>
              <div className={styles.modalBody}>
                <div className={styles.gamesList}>
                  {(() => {
                    // Get completed games for this team
                    const teamEvents = events.filter(event => {
                      if (event.teams && event.teams.home && event.teams.away) {
                        const homeTeamMatch = (event.teams.home.name === modalTeam?.name) || 
                                             (event.teams.home._id === modalTeam?._id) ||
                                             (event.teams.home.teamId === modalTeam?._id);
                        const awayTeamMatch = (event.teams.away.name === modalTeam?.name) ||
                                             (event.teams.away._id === modalTeam?._id) ||
                                             (event.teams.away.teamId === modalTeam?._id);
                        return homeTeamMatch || awayTeamMatch;
                      }
                      return false;
                    });

                    const completedGames = teamEvents.filter(event => {
                      const isCompleted = event.status === 'Completed';
                      const isGame = event.type && !event.type.toLowerCase().includes('practice');
                      
                      // Check for scorecard data
                      let hasScorecard = false;
                      if (event.liveSession?.scorecard?.score && 
                          event.liveSession.scorecard.score.home !== undefined && 
                          event.liveSession.scorecard.score.away !== undefined) {
                        hasScorecard = true;
                      } else if (event.finalResults && 
                                 event.finalResults.homeScore !== undefined && 
                                 event.finalResults.awayScore !== undefined) {
                        hasScorecard = true;
                      } else if (event.scorecard) {
                        if (event.scorecard.score && 
                            (event.scorecard.score.home !== undefined && event.scorecard.score.away !== undefined)) {
                          hasScorecard = true;
                        } else if (event.scorecard.home !== undefined && event.scorecard.away !== undefined) {
                          hasScorecard = true;
                        }
                      }
                      
                      return isCompleted && isGame && hasScorecard;
                    });

                    // Group games by result
                    const wonGames = [];
                    const lostGames = [];
                    const drawGames = [];

                    completedGames.forEach(event => {
                      let home, away;
                      
                      // Extract scores
                      if (event.liveSession?.scorecard?.score && 
                          event.liveSession.scorecard.score.home !== undefined && 
                          event.liveSession.scorecard.score.away !== undefined) {
                        home = event.liveSession.scorecard.score.home;
                        away = event.liveSession.scorecard.score.away;
                      } else if (event.finalResults && 
                                 event.finalResults.homeScore !== undefined && 
                                 event.finalResults.awayScore !== undefined) {
                        home = event.finalResults.homeScore;
                        away = event.finalResults.awayScore;
                      } else if (event.scorecard) {
                        if (event.scorecard.score && 
                            event.scorecard.score.home !== undefined && 
                            event.scorecard.score.away !== undefined) {
                          home = event.scorecard.score.home;
                          away = event.scorecard.score.away;
                        } else if (event.scorecard.home !== undefined && 
                                   event.scorecard.away !== undefined) {
                          home = event.scorecard.home;
                          away = event.scorecard.away;
                        }
                      }

                      if (home !== undefined && away !== undefined) {
                        const isHomeTeam = (event.teams.home.name === modalTeam?.name) || 
                                          (event.teams.home._id === modalTeam?._id) ||
                                          (event.teams.home.teamId === modalTeam?._id);
                        
                        const teamScore = isHomeTeam ? home : away;
                        const opponentScore = isHomeTeam ? away : home;
                        const opponent = isHomeTeam ? event.teams.away.name : event.teams.home.name;
                        
                        const gameData = {
                          ...event,
                          teamScore,
                          opponentScore,
                          opponent,
                          result: teamScore > opponentScore ? 'win' : teamScore < opponentScore ? 'loss' : 'draw'
                        };

                        if (gameData.result === 'win') wonGames.push(gameData);
                        else if (gameData.result === 'loss') lostGames.push(gameData);
                        else drawGames.push(gameData);
                      }
                    });

                    return (
                      <>
                        {wonGames.length > 0 && (
                          <div className={`${styles.gameGroup} ${styles.gameGroupWin}`}>
                            <h3 className={styles.gameGroupTitle}>
                              <FaCheck className={styles.gameGroupIcon} /> Won ({wonGames.length})
                            </h3>
                            {wonGames.map((game, index) => (
                              <div key={index} className={styles.gameCard}>
                                <div className={styles.gameHeader}>
                                  <span className={styles.gameTitle}>{game.title}</span>
                                  <span className={styles.gameDate}>
                                    {game.startTime ? new Date(game.startTime).toLocaleDateString() : 
                                     game.date ? new Date(game.date).toLocaleDateString() : 
                                     game.createdAt ? new Date(game.createdAt).toLocaleDateString() : 
                                     'No date'}
                                  </span>
                                </div>
                                <div className={styles.gameDetails}>
                                  <span className={styles.gameOpponent}>vs {game.opponent}</span>
                                  <span className={styles.gameScore}>
                                    {game.teamScore} - {game.opponentScore}
                                  </span>
                                  <span className={styles.gameType}>{game.type}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {lostGames.length > 0 && (
                          <div className={`${styles.gameGroup} ${styles.gameGroupLoss}`}>
                            <h3 className={styles.gameGroupTitle}>
                              <FaX className={styles.gameGroupIcon} /> Lost ({lostGames.length})
                            </h3>
                            {lostGames.map((game, index) => (
                              <div key={index} className={styles.gameCard}>
                                <div className={styles.gameHeader}>
                                  <span className={styles.gameTitle}>{game.title}</span>
                                  <span className={styles.gameDate}>
                                    {game.startTime ? new Date(game.startTime).toLocaleDateString() : 
                                     game.date ? new Date(game.date).toLocaleDateString() : 
                                     game.createdAt ? new Date(game.createdAt).toLocaleDateString() : 
                                     'No date'}
                                  </span>
                                </div>
                                <div className={styles.gameDetails}>
                                  <span className={styles.gameOpponent}>vs {game.opponent}</span>
                                  <span className={styles.gameScore}>
                                    {game.teamScore} - {game.opponentScore}
                                  </span>
                                  <span className={styles.gameType}>{game.type}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {drawGames.length > 0 && (
                          <div className={`${styles.gameGroup} ${styles.gameGroupDraw}`}>
                            <h3 className={styles.gameGroupTitle}>
                              <FaHandshake className={styles.gameGroupIcon} /> Draw ({drawGames.length})
                            </h3>
                            {drawGames.map((game, index) => (
                              <div key={index} className={styles.gameCard}>
                                <div className={styles.gameHeader}>
                                  <span className={styles.gameTitle}>{game.title}</span>
                                  <span className={styles.gameDate}>
                                    {game.startTime ? new Date(game.startTime).toLocaleDateString() : 
                                     game.date ? new Date(game.date).toLocaleDateString() : 
                                     game.createdAt ? new Date(game.createdAt).toLocaleDateString() : 
                                     'No date'}
                                  </span>
                                </div>
                                <div className={styles.gameDetails}>
                                  <span className={styles.gameOpponent}>vs {game.opponent}</span>
                                  <span className={styles.gameScore}>
                                    {game.teamScore} - {game.opponentScore}
                                  </span>
                                  <span className={styles.gameType}>{game.type}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {completedGames.length === 0 && (
                          <div className={styles.noGamesMessage}>
                            <div className={styles.noGamesIcon}><FaFutbol /></div>
                            <h3>No Completed Games</h3>
                            <p>This team hasn't completed any games yet. Check back after some matches!</p>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}

        {/* Team Invitation Modal */}
        {showInvitationModal && selectedTeamForInvitation && createPortal(
          <TeamInvitationModal
            team={selectedTeamForInvitation}
            clubId={resolvedClubId}
            programId={programId}
            onClose={() => {
              setShowInvitationModal(false);
              setSelectedTeamForInvitation(null);
            }}
            onInvitationSent={(invitation) => {
              // Optionally refresh team data or show success message
            }}
          />,
          document.body
        )}

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          isOpen={showConfirmDelete}
          onClose={() => {
            setShowConfirmDelete(false);
            setTeamToDelete(null);
          }}
          onConfirm={deleteTeam}
          title="Delete Team"
          message={teamToDelete ? `Are you sure you want to delete "${teamToDelete.name}"? This action cannot be undone.` : ''}
        />
      </div>
    </div>
  );
};

export default TeamsPage;
